import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import i18n from 'i18next';
import { useAppStore } from './appStore';
import { AuthMode, AuthUser, Locale } from '../types';
import { resetAllProductRelatedStores } from '../utils';
import { useArrivalReceiptStore } from './arrivalReceiptStore';
import { isAdminGroup, resolveRoles } from '../utils/permissions';
import { getMe } from '../services/api/auth';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  stationId: string | null;
  authMode: AuthMode | null;

  setStationId: (stationId: string | null) => void;
  setAuthMode: (authMode: AuthMode | null) => void;
  restoreSession: () => Promise<void>;
  setAuthenticatedUser: (user: AuthUser) => void;
  syncLocale: () => Promise<void>;
  setLocale: (locale: Locale) => void;
  clearAuthState: () => void;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      status: 'loading',
      user: null,
      stationId: null,
      authMode: null,

      setStationId: (stationId) => set({ stationId }),
      setAuthMode: (authMode) => set({ authMode }),

      setAuthenticatedUser: (user) => {
        set({
          user,
          status: 'authenticated',
        });
        void get().syncLocale();
      },

      syncLocale: async () => {
        try {
          const profile = await getMe();
          const locale = profile.locale ?? 'es';
          i18n.changeLanguage(locale);
          set((state) => ({
            user: state.user ? { ...state.user, locale } : null,
          }));
        } catch {
          // keep whichever locale is currently active if the profile call fails
        }
      },

      setLocale: (locale) => {
        i18n.changeLanguage(locale);
        set((state) => ({
          user: state.user ? { ...state.user, locale } : null,
        }));
      },

      clearAuthState: () =>
        set({
          user: null,
          status: 'unauthenticated',
          stationId: null,
          authMode: null,
        }),

      restoreSession: async () => {
        try {
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;

          if (!idToken) {
            set({
              user: null,
              status: 'unauthenticated',
            });
            return;
          }

          const payload = idToken.payload as Record<string, unknown>;
          const groups = (payload['cognito:groups'] as string[]) ?? [];
          const username = (payload['cognito:username'] ||
            payload.name ||
            payload.email ||
            '') as string;
          const user: AuthUser = {
            id: (payload.name as string) || (payload.email as string) || '',
            username,
            email: (payload.email as string) || undefined,
            name: (payload.name as string) || (payload.email as string) || '',
            sub: (payload.sub as string) || '',
            roles: resolveRoles(groups),
            isAdmin: isAdminGroup(groups),
            locale: 'es',
          };

          set({
            user,
            status: 'authenticated',
          });
          void get().syncLocale();
        } catch {
          set({
            user: null,
            status: 'unauthenticated',
          });
        }
      },

      logout: async () => {
        try {
          await signOut();
        } catch {
          // ignore remote sign-out failure, still clear local state
        }

        set({
          user: null,
          status: 'unauthenticated',
          stationId: null,
          authMode: null,
        });

        resetAllProductRelatedStores();
        useAppStore.getState().reset();
        useArrivalReceiptStore.getState().setCurrentArrivalReceipt(null);
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        stationId: state.stationId,
        authMode: state.authMode,
      }),
    }
  )
);
